import { removeUser, updateUser, inviteUser } from '@api/users';
import { ResponseErrorDto } from '@interfaces/models';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from '@ui/components';

export const useUsers = ({
  onCreateUserSuccess,
  onRemoveUserSuccess,
  onEditUserSuccess,
  onRemoveUserError,
  onCreateUserError,
  onEditUserError,
}: {
  onCreateUserError?: (error: ResponseErrorDto) => void;
  onRemoveUserError?: (error: ResponseErrorDto) => void;
  onEditUserError?: (error: ResponseErrorDto) => void;
  onCreateUserSuccess?: () => void;
  onRemoveUserSuccess?: () => void;
  onEditUserSuccess?: () => void;
}) => {
  const { t } = useTranslation();

  const { mutate: removeUserMutation, isPending: isRemoveUserPending } = useMutation({
    mutationFn: (userId: string) => removeUser(userId),
    onSuccess: () => {
      onRemoveUserSuccess?.();
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      onRemoveUserError?.(error);
      toast.error(t(`API_ERRORS.DEFAULT`));
    },
  });

  const { mutate: editUserMutation, isPending: isEditUserPending } = useMutation({
    mutationFn: updateUser,
    onSuccess: () => {
      onEditUserSuccess?.();
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      onEditUserError?.(error);
      toast.error(t(`API_ERRORS.DEFAULT`));
    },
  });

  const { mutate: createUserMutation, isPending: isCreateUserPending } = useMutation({
    mutationFn: inviteUser,
    onSuccess: () => {
      onCreateUserSuccess?.();
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      onCreateUserError?.(error);
      toast.error(t(`API_ERRORS.DEFAULT`));
    },
  });

  return {
    removeUser: removeUserMutation,
    createUser: createUserMutation,
    editUser: editUserMutation,
    isRemoveUserPending,
    isCreateUserPending,
    isEditUserPending,
  };
};
